import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  HiOutlineServer, HiOutlineTrash, HiOutlinePlus,
  HiOutlineDocumentText, HiOutlineChartBar,
  HiOutlineViewGrid, HiOutlinePencil, HiOutlineCheck, HiOutlineX
} from 'react-icons/hi';
import {
  getProduct, updateProduct, deleteProduct, getCloudRunsForProject,
  getCloudRunInfo, getCloudRunLogs, getCloudRunMetrics, getCloudRunCosts
} from '../services/api';
import { TIME_PRESETS, getPresetRange } from '../shared/constants';
import selectStyles from '../shared/selectStyles';
import DashboardTab from './tabs/DashboardTab';
import ServicesTab from './tabs/ServicesTab';
import LogsTab from './tabs/LogsTab';
import MetricsTab from './tabs/MetricsTab';
import './ProductView.css';

function ProductView({ onProductUpdated, gcpProjects }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Inline rename
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef(null);

  // Dashboard tab state
  const [dashErrors, setDashErrors] = useState([]);
  const [dashErrorsLoading, setDashErrorsLoading] = useState(false);
  const [dashCosts, setDashCosts] = useState(null);
  const [dashCostsLoading, setDashCostsLoading] = useState(false);
  const [dashErrorsLastRefreshed, setDashErrorsLastRefreshed] = useState(null);
  const [dashCostsLastRefreshed, setDashCostsLastRefreshed] = useState(null);
  const [dashAutoRefresh, setDashAutoRefresh] = useState(true);
  const [costMonth, setCostMonth] = useState(0);
  const errorsIntervalRef = useRef(null);
  const costsIntervalRef = useRef(null);

  // Add services state
  const [showAddServices, setShowAddServices] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [cloudRunOptions, setCloudRunOptions] = useState([]);
  const [selectedCloudRuns, setSelectedCloudRuns] = useState([]);
  const [loadingCloudRuns, setLoadingCloudRuns] = useState(false);

  // Services tab
  const [expandedServices, setExpandedServices] = useState({});
  const [serviceInfoCache, setServiceInfoCache] = useState({});
  const [loadingServiceInfo, setLoadingServiceInfo] = useState({});

  // Logs tab state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logServiceFilter, setLogServiceFilter] = useState(null);
  const [logTimePreset, setLogTimePreset] = useState('1h');
  const [logStartTime, setLogStartTime] = useState('');
  const [logEndTime, setLogEndTime] = useState('');
  const [logsPageSize, setLogsPageSize] = useState(50);
  const [logsCurrentPage, setLogsCurrentPage] = useState(1);
  const [logSortByService, setLogSortByService] = useState(false);
  const [logSeverityFilter, setLogSeverityFilter] = useState([]);
  const [showLogTimePresets, setShowLogTimePresets] = useState(false);
  const [showLogSeverityDropdown, setShowLogSeverityDropdown] = useState(false);

  // Metrics tab state
  const [metrics, setMetrics] = useState(null);
  const [metricsTimeRange, setMetricsTimeRange] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsServiceFilter, setMetricsServiceFilter] = useState(null);
  const [metricsTimePreset, setMetricsTimePreset] = useState('1h');
  const [metricsStartTime, setMetricsStartTime] = useState('');
  const [metricsEndTime, setMetricsEndTime] = useState('');
  const [showMetricsTimePresets, setShowMetricsTimePresets] = useState(false);

  // Confirm remove state
  const [confirmRemove, setConfirmRemove] = useState(null);

  const fetchProduct = useCallback(async () => {
    try {
      const res = await getProduct(id);
      setProduct(res.data);
    } catch (err) {
      toast.error('Failed to load product');
      navigate('/');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  useEffect(() => {
    if (selectedProject) {
      (async () => {
        setLoadingCloudRuns(true);
        try {
          const res = await getCloudRunsForProject(selectedProject.value);
          setCloudRunOptions(res.data.map((cr) => ({
            value: `${cr.name}|${cr.region}`, label: `${cr.name} (${cr.region})`,
            serviceName: cr.name, region: cr.region,
          })));
        } catch { toast.error('Failed to fetch Cloud Run services'); }
        setLoadingCloudRuns(false);
      })();
    }
  }, [selectedProject]);

  const fetchAllServiceHealth = useCallback(async () => {
    if (!product?.cloudRuns?.length) return;
    const results = await Promise.allSettled(
      product.cloudRuns.map((cr, index) => {
        const key = `svc-${index}`;
        if (serviceInfoCache[key]) return Promise.resolve(null);
        return getCloudRunInfo(cr.gcpProjectId, cr.serviceName, cr.region)
          .then(res => ({ key, data: res.data }));
      })
    );
    const newCache = {};
    results.forEach(r => {
      if (r.status === 'fulfilled' && r.value) {
        newCache[r.value.key] = r.value.data;
      }
    });
    if (Object.keys(newCache).length > 0) {
      setServiceInfoCache(prev => ({ ...prev, ...newCache }));
    }
  }, [product, serviceInfoCache]);

  const toggleService = async (index) => {
    const key = `svc-${index}`;
    const isExpanding = !expandedServices[key];
    setExpandedServices(prev => ({ ...prev, [key]: isExpanding }));

    if (isExpanding && !serviceInfoCache[key]) {
      const cr = product.cloudRuns[index];
      setLoadingServiceInfo(prev => ({ ...prev, [key]: true }));
      try {
        const res = await getCloudRunInfo(cr.gcpProjectId, cr.serviceName, cr.region);
        setServiceInfoCache(prev => ({ ...prev, [key]: res.data }));
      } catch {
        toast.error(`Failed to fetch info for ${cr.serviceName}`);
      }
      setLoadingServiceInfo(prev => ({ ...prev, [key]: false }));
    }
  };

  const fetchLogs = useCallback(async () => {
    if (!product || !product.cloudRuns?.length) return;
    setLogsLoading(true);
    setLogsCurrentPage(1);
    try {
      let startTime, endTime;
      if (logTimePreset === 'custom') {
        startTime = logStartTime || undefined;
        endTime = logEndTime || undefined;
      } else {
        const preset = TIME_PRESETS.find(p => p.value === logTimePreset);
        if (preset) { const r = getPresetRange(preset.hours); startTime = r.startTime; endTime = r.endTime; }
      }
      const isAll = !logServiceFilter || logServiceFilter.value === 'all';
      const services = isAll ? product.cloudRuns : [logServiceFilter];
      const results = await Promise.allSettled(services.map(svc =>
        getCloudRunLogs(svc.gcpProjectId, svc.serviceName, svc.region, {
          startTime, endTime, severity: logSeverityFilter,
        })
      ));
      let failedCount = 0;
      const failedNames = [];
      const allLogs = [];
      results.forEach((result, idx) => {
        const svcName = services[idx].serviceName;
        if (result.status === 'fulfilled') {
          allLogs.push(...(result.value?.data?.logs || []));
        } else {
          failedCount++;
          failedNames.push(svcName);
        }
      });
      if (failedCount > 0 && failedCount < services.length) {
        toast.warning(`Logs fetched but failed for: ${failedNames.join(', ')}`);
      } else if (failedCount === services.length) {
        toast.error(`Failed to fetch logs from all services: ${failedNames.join(', ')}`);
      }
      allLogs.sort((a, b) => {
        const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        if (isNaN(ta) && isNaN(tb)) return 0;
        if (isNaN(ta)) return 1;
        if (isNaN(tb)) return -1;
        return tb - ta;
      });
      setLogs(allLogs);
    } catch { toast.error('Failed to fetch logs'); }
    setLogsLoading(false);
  }, [product, logServiceFilter, logTimePreset, logStartTime, logEndTime, logSeverityFilter]);

  const fetchMetrics = useCallback(async () => {
    if (!product || !product.cloudRuns?.length) return;
    setMetricsLoading(true);
    try {
      let startTime, endTime;
      if (metricsTimePreset === 'custom') {
        startTime = metricsStartTime || undefined;
        endTime = metricsEndTime || undefined;
      } else {
        const preset = TIME_PRESETS.find(p => p.value === metricsTimePreset);
        if (preset) { const r = getPresetRange(preset.hours); startTime = r.startTime; endTime = r.endTime; }
      }
      const isAll = !metricsServiceFilter || metricsServiceFilter.value === 'all';
      if (isAll) {
        const results = await Promise.all(product.cloudRuns.map(svc =>
          getCloudRunMetrics(svc.gcpProjectId, svc.serviceName, svc.region, { startTime, endTime })
            .catch(() => ({ data: { metrics: {}, timeRange: null } }))
        ));
        const merged = {};
        let tr = null;
        for (const res of results) {
          if (res.data.timeRange && !tr) tr = res.data.timeRange;
          for (const [name, data] of Object.entries(res.data.metrics || {})) {
            if (!merged[name]) { merged[name] = { ...data, points: [...(data.points || [])] }; }
            else { merged[name].points = [...merged[name].points, ...(data.points || [])]; merged[name].totalDataPoints = merged[name].points.length; }
          }
        }
        for (const name of Object.keys(merged)) {
          merged[name].points.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        }
        setMetrics(merged);
        setMetricsTimeRange(tr);
      } else {
        const svc = metricsServiceFilter;
        const res = await getCloudRunMetrics(svc.gcpProjectId, svc.serviceName, svc.region, { startTime, endTime });
        setMetrics(res.data.metrics);
        setMetricsTimeRange(res.data.timeRange);
      }
    } catch { toast.error('Failed to fetch metrics'); }
    setMetricsLoading(false);
  }, [product, metricsServiceFilter, metricsTimePreset, metricsStartTime, metricsEndTime]);

  const fetchDashErrors = useCallback(async () => {
    if (!product || !product.cloudRuns?.length) return;
    setDashErrorsLoading(true);
    try {
      const now = new Date();
      const startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const endTime = now.toISOString();
      const results = await Promise.allSettled(
        product.cloudRuns.map(svc =>
          getCloudRunLogs(svc.gcpProjectId, svc.serviceName, svc.region, {
            startTime, endTime, severity: ['ERROR', 'CRITICAL'], maxLogs: 100,
          })
        )
      );
      const allErrors = [];
      results.forEach((r) => {
        if (r.status === 'fulfilled' && r.value?.data?.logs) {
          allErrors.push(...r.value.data.logs);
        }
      });
      allErrors.sort((a, b) => {
        const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
      });
      setDashErrors(allErrors.slice(0, 50));
    } catch { setDashErrors([]); }
    setDashErrorsLoading(false);
    setDashErrorsLastRefreshed(new Date());
  }, [product]);

  const fetchDashCosts = useCallback(async () => {
    if (!product || !product.cloudRuns?.length) return;
    setDashCostsLoading(true);
    try {
      const grouped = {};
      for (const cr of product.cloudRuns) {
        if (!grouped[cr.gcpProjectId]) grouped[cr.gcpProjectId] = { names: [], regions: [] };
        grouped[cr.gcpProjectId].names.push(cr.serviceName);
        grouped[cr.gcpProjectId].regions.push(cr.region);
      }
      const costResults = await Promise.allSettled(
        Object.entries(grouped).map(([projId, { names, regions }]) =>
          getCloudRunCosts(projId, names, regions, { month: costMonth })
        )
      );
      const merged = { currentMonth: {}, previousMonth: {} };
      let currentPeriod = '', prevPeriod = '', exchangeRate = 85, source = 'estimated';
      costResults.forEach(r => {
        if (r.status === 'fulfilled' && r.value?.data) {
          const d = r.value.data;
          currentPeriod = d.currentMonth?.period || currentPeriod;
          prevPeriod = d.previousMonth?.period || prevPeriod;
          exchangeRate = d.exchangeRate || exchangeRate;
          source = d.source || source;
          Object.assign(merged.currentMonth, d.currentMonth?.services || {});
          Object.assign(merged.previousMonth, d.previousMonth?.services || {});
        }
      });
      setDashCosts({
        exchangeRate, source, monthOffset: costMonth,
        currentMonth: { period: currentPeriod, services: merged.currentMonth },
        previousMonth: { period: prevPeriod, services: merged.previousMonth },
      });
    } catch { setDashCosts(null); }
    setDashCostsLoading(false);
    setDashCostsLastRefreshed(new Date());
  }, [product, costMonth]);

  useEffect(() => {
    setLogs([]);
    setMetrics({});
    setDashErrors([]);
    setDashCosts(null);
    setCostMonth(0);
    setLogsCurrentPage(1);
    setLogServiceFilter(null);
    setMetricsServiceFilter(null);
    setLogSortByService(false);
  }, [id]);

  useEffect(() => {
    if (activeTab === 'dashboard' && product) {
      fetchDashErrors();
      fetchDashCosts();
      fetchAllServiceHealth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, product]);

  useEffect(() => {
    if (activeTab === 'dashboard' && product) {
      fetchDashCosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costMonth]);

  // Auto-refresh: errors every 2min, costs every 10min
  useEffect(() => {
    if (errorsIntervalRef.current) clearInterval(errorsIntervalRef.current);
    if (costsIntervalRef.current) clearInterval(costsIntervalRef.current);
    if (activeTab === 'dashboard' && product && dashAutoRefresh) {
      errorsIntervalRef.current = setInterval(() => { fetchDashErrors(); }, 120000);
      costsIntervalRef.current = setInterval(() => { fetchDashCosts(); }, 600000);
    }
    return () => {
      if (errorsIntervalRef.current) clearInterval(errorsIntervalRef.current);
      if (costsIntervalRef.current) clearInterval(costsIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, product, dashAutoRefresh]);

  useEffect(() => {
    if (activeTab === 'logs' && product) fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, product, logServiceFilter, logTimePreset, logSeverityFilter]);

  useEffect(() => {
    if (activeTab === 'metrics' && product) fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, product, metricsServiceFilter, metricsTimePreset]);

  // Click-through from dashboard summary cards
  const handleSwitchTab = useCallback((tab, opts) => {
    setActiveTab(tab);
    if (tab === 'logs' && opts?.severity) {
      setLogSeverityFilter(opts.severity);
      setLogTimePreset('1d');
    }
  }, []);

  const handleAddServices = async () => {
    if (selectedCloudRuns.length === 0) return;
    const existing = product.cloudRuns || [];
    const newServices = [];
    const duplicates = [];
    for (const cr of selectedCloudRuns) {
      const isDuplicate = existing.some(
        e => e.serviceName === cr.serviceName && e.region === cr.region && e.gcpProjectId === selectedProject.value
      );
      if (isDuplicate) duplicates.push(cr.serviceName);
      else newServices.push({
        serviceName: cr.serviceName, region: cr.region,
        gcpProjectId: selectedProject.value, gcpProjectName: selectedProject.projectName,
      });
    }
    if (duplicates.length > 0 && newServices.length === 0) {
      toast.warning(`Already added: ${duplicates.join(', ')}`);
      return;
    }
    if (duplicates.length > 0) toast.warning(`Skipped (already added): ${duplicates.join(', ')}`);
    try {
      await updateProduct(id, { cloudRuns: [...existing, ...newServices] });
      toast.success(`${newServices.length} service(s) added`);
      fetchProduct(); onProductUpdated();
      setShowAddServices(false); setSelectedCloudRuns([]);
    } catch { toast.error('Failed to add services'); }
  };

  const handleRemoveService = (e, index) => {
    e.stopPropagation();
    setConfirmRemove(index);
  };

  const confirmRemoveService = async () => {
    if (confirmRemove === null) return;
    const updated = [...product.cloudRuns];
    updated.splice(confirmRemove, 1);
    try {
      await updateProduct(id, { cloudRuns: updated });
      toast.success('Service removed');
      fetchProduct(); onProductUpdated();
    } catch { toast.error('Failed to remove service'); }
    setConfirmRemove(null);
  };

  const handleDeleteProduct = async () => {
    if (!window.confirm(`Delete product "${product.name}"?`)) return;
    try {
      await deleteProduct(id);
      toast.success('Product deleted');
      onProductUpdated(); navigate('/');
    } catch { toast.error('Failed to delete product'); }
  };

  const startRename = () => {
    setRenameValue(product.name);
    setIsRenaming(true);
    setTimeout(() => renameInputRef.current?.focus(), 50);
  };

  const submitRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === product.name) {
      setIsRenaming(false);
      return;
    }
    try {
      await updateProduct(id, { name: trimmed });
      toast.success('Product renamed');
      fetchProduct();
      onProductUpdated();
    } catch { toast.error('Failed to rename product'); }
    setIsRenaming(false);
  };

  const cancelRename = () => {
    setIsRenaming(false);
  };

  if (loading) return (
    <div style={{ padding: '0 0 40px' }}>
      <div className="skeleton-row" style={{ marginBottom: 24 }}>
        <div className="skeleton skeleton-text-lg" style={{ width: 200 }}></div>
      </div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-color)', marginBottom: 24 }}>
        {[1, 2, 3, 4].map(i => <div key={i} className="skeleton" style={{ width: 90, height: 36, marginRight: 8, borderRadius: 0 }}></div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-card">
            <div className="skeleton-row">
              <div className="skeleton skeleton-circle"></div>
              <div style={{ flex: 1 }}>
                <div className="skeleton skeleton-text-lg" style={{ width: '50%' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '70%' }}></div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="skeleton-card">
        {[1, 2, 3].map(i => (
          <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
            <div className="skeleton skeleton-text" style={{ width: `${80 - i * 15}%` }}></div>
          </div>
        ))}
      </div>
    </div>
  );
  if (!product) return null;

  const projectOptions = gcpProjects.map((p) => ({
    value: p._id, label: `${p.projectName} (${p.projectId})`, projectName: p.projectName,
  }));

  return (
    <div className="product-view" onClick={() => { setShowLogTimePresets(false); setShowLogSeverityDropdown(false); setShowMetricsTimePresets(false); }}>
      {/* Header */}
      <div className="pv-header">
        <div className="pv-header-left">
          {isRenaming ? (
            <div className="pv-rename-row">
              <input
                ref={renameInputRef}
                className="pv-rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') cancelRename(); }}
              />
              <button className="btn btn-sm btn-icon pv-rename-ok" onClick={submitRename} title="Save"><HiOutlineCheck size={16} /></button>
              <button className="btn btn-sm btn-icon pv-rename-cancel" onClick={cancelRename} title="Cancel"><HiOutlineX size={16} /></button>
            </div>
          ) : (
            <div className="pv-name-row">
              <h1>{product.name}</h1>
              <button className="btn btn-sm btn-icon pv-rename-btn" onClick={startRename} title="Rename product"><HiOutlinePencil size={14} /></button>
            </div>
          )}
          <span className="pv-service-count">{product.cloudRuns?.length || 0} services</span>
        </div>
        <div className="pv-header-actions">
          <button className="btn btn-primary btn-sm" onClick={() => {
            setShowAddServices(true);
            if (product?.cloudRuns?.length > 0 && !selectedProject) {
              const existingProjectId = product.cloudRuns[0].gcpProjectId;
              const match = projectOptions.find((p) => p.value === existingProjectId);
              if (match) setSelectedProject(match);
            }
          }}>
            <HiOutlinePlus size={15} /> Add Services
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleDeleteProduct}>
            <HiOutlineTrash size={15} />
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
          <HiOutlineViewGrid size={16} /> Dashboard
        </button>
        <button className={`tab ${activeTab === 'services' ? 'active' : ''}`} onClick={() => setActiveTab('services')}>
          <HiOutlineServer size={16} /> Services
        </button>
        <button className={`tab ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>
          <HiOutlineDocumentText size={16} /> Logs
        </button>
        <button className={`tab ${activeTab === 'metrics' ? 'active' : ''}`} onClick={() => setActiveTab('metrics')}>
          <HiOutlineChartBar size={16} /> Metrics
        </button>
      </div>

      {/* Add Services Panel */}
      {showAddServices && (
        <div className="card add-services-panel">
          <h3>Add Cloud Run Services</h3>
          <div className="form-group" style={{ marginTop: 16 }}>
            <label>GCP Project</label>
            <Select styles={selectStyles} options={projectOptions} value={selectedProject} onChange={setSelectedProject} placeholder="Select a GCP project..." isClearable isDisabled={product?.cloudRuns?.length > 0} />
          </div>
          {selectedProject && (
            <div className="form-group">
              <label>Cloud Run Services</label>
              {loadingCloudRuns ? (
                <div className="loading-inline"><div className="spinner" style={{ width: 20, height: 20 }}></div><span>Loading services...</span></div>
              ) : (
                <Select styles={selectStyles} options={cloudRunOptions} value={selectedCloudRuns} onChange={setSelectedCloudRuns} placeholder="Select services..." isMulti closeMenuOnSelect={false} />
              )}
            </div>
          )}
          <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
            <button className="btn btn-primary btn-sm" onClick={handleAddServices}>Add Selected</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowAddServices(false); setSelectedCloudRuns([]); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'dashboard' && (
        <DashboardTab
          product={product}
          dashErrors={dashErrors} dashErrorsLoading={dashErrorsLoading}
          dashCosts={dashCosts} dashCostsLoading={dashCostsLoading}
          dashErrorsLastRefreshed={dashErrorsLastRefreshed}
          dashCostsLastRefreshed={dashCostsLastRefreshed}
          dashAutoRefresh={dashAutoRefresh} setDashAutoRefresh={setDashAutoRefresh}
          fetchDashErrors={fetchDashErrors} fetchDashCosts={fetchDashCosts}
          costMonth={costMonth} setCostMonth={setCostMonth}
          onSwitchTab={handleSwitchTab}
          serviceInfoCache={serviceInfoCache}
        />
      )}

      {activeTab === 'services' && (
        <ServicesTab
          product={product}
          expandedServices={expandedServices}
          serviceInfoCache={serviceInfoCache}
          loadingServiceInfo={loadingServiceInfo}
          toggleService={toggleService}
          handleRemoveService={handleRemoveService}
        />
      )}

      {activeTab === 'logs' && (
        <LogsTab
          product={product} logs={logs} logsLoading={logsLoading} fetchLogs={fetchLogs}
          logServiceFilter={logServiceFilter} setLogServiceFilter={setLogServiceFilter}
          logTimePreset={logTimePreset} setLogTimePreset={setLogTimePreset}
          logStartTime={logStartTime} setLogStartTime={setLogStartTime}
          logEndTime={logEndTime} setLogEndTime={setLogEndTime}
          logSeverityFilter={logSeverityFilter} setLogSeverityFilter={setLogSeverityFilter}
          showLogTimePresets={showLogTimePresets} setShowLogTimePresets={setShowLogTimePresets}
          showLogSeverityDropdown={showLogSeverityDropdown} setShowLogSeverityDropdown={setShowLogSeverityDropdown}
          logSortByService={logSortByService} setLogSortByService={setLogSortByService}
          logsPageSize={logsPageSize} setLogsPageSize={setLogsPageSize}
          logsCurrentPage={logsCurrentPage} setLogsCurrentPage={setLogsCurrentPage}
        />
      )}

      {activeTab === 'metrics' && (
        <MetricsTab
          product={product} metrics={metrics} metricsLoading={metricsLoading}
          metricsTimeRange={metricsTimeRange} fetchMetrics={fetchMetrics}
          metricsServiceFilter={metricsServiceFilter} setMetricsServiceFilter={setMetricsServiceFilter}
          metricsTimePreset={metricsTimePreset} setMetricsTimePreset={setMetricsTimePreset}
          metricsStartTime={metricsStartTime} setMetricsStartTime={setMetricsStartTime}
          metricsEndTime={metricsEndTime} setMetricsEndTime={setMetricsEndTime}
          showMetricsTimePresets={showMetricsTimePresets} setShowMetricsTimePresets={setShowMetricsTimePresets}
        />
      )}

      {/* Remove Service Confirmation Modal */}
      {confirmRemove !== null && product?.cloudRuns?.[confirmRemove] && (
        <div className="modal-overlay" onClick={() => setConfirmRemove(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon danger">
              <HiOutlineTrash size={28} />
            </div>
            <h3>Remove Service</h3>
            <p>Are you sure you want to remove <strong>{product.cloudRuns[confirmRemove].serviceName}</strong> from this product?</p>
            <p className="confirm-sub">This will only remove the service from tracking. The Cloud Run service itself will not be affected.</p>
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={() => setConfirmRemove(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmRemoveService}>Yes, Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductView;
